import { BRAND } from '@app/brand'
import { detectLang, getLang, onLangChange, setLang } from '@app/i18n'
import { buildGraph, isParking, localized, parseVenue, type Venue } from '@app/rvf'
import { render } from 'preact'
import { reportScan } from './analytics'
import { App } from './app'
import { loadAssistant, watchEmergencyClass } from './assistant'
import { carParam, loadCar } from './car'
import { DEFAULT_VENUE_ID, venueDataUrl } from './config'
import { importChunk } from './lazy-retry'
import { parseVenuePath } from './link/venue-link'
import { loadAlertProfile } from './nav/alerts'
import { loadOccupants } from './occupants'
import { loadShoppingList } from './shopping'
import {
  alertProfile,
  anchorId,
  appState,
  emergencyDrill,
  emergencyFrame,
  emergencyHazard,
  geoStart,
  graph,
  isOffline,
  langTick,
  parkedCar,
  pendingTargetId,
  swReady,
  updateReady,
  venue,
  venueMap,
  venueVerified,
} from './state'
import './terra.css'
import './aurora.css'
import './components.css'
import './widgets.css'
import './emergency.css'
import './emergency-widgets.css'
import './styles.css'
import './header.css'
import './target-card.css'
import './orient.css'
import './route.css'
import './hud.css'
import './ar.css'
import './search.css'
import { loadCategories } from './categories'
import './directory.css'
import './volume-switch.css'
import './viewer-extras.css'
import './promo.css'
import './terra-panels.css'
import './mall.css'
import './cards.css'
import './card-access.css'
import './shopping.css'

/** Восстановление места машины: сохранённое в браузере или из `?car=` (durability). */
function restoreCar(parsed: Venue): void {
  const stored = loadCar(parsed.venue.id)
  if (stored && parsed.units.some((u) => u.id === stored.targetId)) {
    parkedCar.value = stored
    return
  }
  const fromUrl = carParam(location.search)
  const unit = fromUrl
    ? parsed.units.find((u) => u.id === fromUrl && isParking(parsed, u.category))
    : null
  if (unit) {
    parkedCar.value = {
      venueId: parsed.venue.id,
      targetId: unit.id,
      levelId: unit.level,
      label: unit.name['ru'] ?? unit.id,
      savedAt: '',
    }
  }
}

const LANG_STORE_KEY = 'lang'

function updateTitle(): void {
  const v = venue.value
  document.title = v
    ? `${localized(v.venue.name, getLang(), v.venue.default_language)} — ${BRAND.productName}`
    : BRAND.productName
}

setLang(detectLang(localStorage.getItem(LANG_STORE_KEY), navigator.languages ?? []))
// Справочник категорий (U7) — в фоне: карта не ждёт его, подписи обновятся сами.
loadCategories()
document.documentElement.lang = getLang()
onLangChange(() => {
  localStorage.setItem(LANG_STORE_KEY, getLang())
  document.documentElement.lang = getLang()
  langTick.value++
  venueMap.value?.refreshLabels()
  updateTitle()
})
updateTitle()

/** Гео-старт для карточки цели — только при УЖЕ выданном разрешении (без промпта). */
async function cacheGeoStartIfGranted(): Promise<void> {
  if (!('permissions' in navigator) || !navigator.geolocation) return
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    if (status.state !== 'granted') return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        geoStart.value = [pos.coords.longitude, pos.coords.latitude]
      },
      () => undefined,
      { maximumAge: 60_000, timeout: 5_000 },
    )
  } catch {
    // permissions API недоступен — старт останется от якоря/входа
  }
}
void cacheGeoStartIfGranted()

// H1: живой индикатор сети — бейдж переключается на «карта из кеша».
window.addEventListener('offline', () => {
  isOffline.value = true
})
window.addEventListener('online', () => {
  isOffline.value = false
})

// Оффлайн-бейдж честен только после реальной готовности service worker.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker.ready.then((reg) => {
    swReady.value = true
    // Живущая вкладка/PWA не перезагружает страницу — сама проверяет новый
    // билд: раз в минуту, при возврате вкладки из фона и из BFCache
    // (иначе свежий деплой виден только после жёсткого рефреша).
    const check = (): void => {
      void reg.update().catch(() => {
        // оффлайн/сеть моргнула — проверим в следующий раз
      })
    }
    setInterval(check, 60_000)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check()
    })
    window.addEventListener('pageshow', (e) => {
      if ((e as PageTransitionEvent).persisted) check()
    })
  })
  // Новый деплой активировал новый SW под живой страницей (autoUpdate +
  // skipWaiting): без reload вкладка остаётся на старом билде до следующего
  // визита. В спокойных состояниях перезагружаемся тихо; посреди ведения /
  // выбора цели не рвём поток — показываем вежливый промт «Обновить»
  // (updateReady → тост в App), пользователь решает сам.
  let hadController = navigator.serviceWorker.controller !== null
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      // Первая установка SW на чистом визите — контроллер появился, это не апдейт.
      hadController = true
      return
    }
    const calm =
      appState.value === 'overview' || appState.value === 'loading' || appState.value === 'error'
    if (calm) {
      location.reload()
    } else {
      updateReady.value = true
    }
  })
}

alertProfile.value = loadAlertProfile()

// Корень без площадки — глобальный каталог listed-площадок (запрос 24.07);
// контракт /v/{venue}?a=&to= неизменен (ADR 0004).
const parsedLink = parseVenuePath(location.pathname, location.search)
const isDirectory = parsedLink === null && location.pathname === '/'
// Раскрытие ранжирования (UCPD 7(4a)): отдельный адрес, чтобы ссылка из
// рекламного блока работала и когда карта ещё не загрузилась.
const isPromoRanking = location.pathname.replace(/\/$/, '') === '/promo-ranking'
const link = parsedLink ?? { venueId: DEFAULT_VENUE_ID }
anchorId.value = link.anchor ?? null
pendingTargetId.value = link.to ?? null
// Открытие с QR-якорем = скан щита — единственная метрика полевого теста.
if (link.anchor) reportScan(link.venueId, link.anchor)

const root = document.getElementById('app')
if (root) {
  root.replaceChildren()
  if (isPromoRanking) {
    void importChunk(() => import('./ui/promo-ranking')).then(({ PromoRankingPage }) => {
      render(<PromoRankingPage />, root)
    })
  } else if (isDirectory) {
    void importChunk(() => import('./ui/directory')).then(({ DirectoryPage }) => {
      render(<DirectoryPage />, root)
    })
  } else {
    render(<App />, root)
  }
}

async function loadVenue(venueId: string): Promise<void> {
  try {
    const response = await fetch(venueDataUrl(venueId))
    if (!response.ok) throw new Error(`venue fetch: HTTP ${response.status}`)
    const parsed = parseVenue(await response.json())
    venue.value = parsed
    graph.value = buildGraph(parsed.network)
    restoreCar(parsed)
    // Список покупок: ?list= из расшаренной ссылки сильнее сохранённого.
    loadShoppingList(parsed, new URLSearchParams(location.search).get('list'))
    appState.value = 'overview'
    updateTitle()
    // Живой слой карточек — после карты, не блокируя её (ADR 0014).
    void loadOccupants(parsed.venue.id)
    // AI-ассистент (ADR 0020): конфиг после карты, скрипт — в idle-паузу;
    // класс ЧС ведём в любом случае (скрывает виджет в аварийном режиме).
    watchEmergencyClass()
    void loadAssistant(parsed.venue.id)
    // Живая тревога владельца: лёгкий опрос — карта обязана узнать о пожаре,
    // даже если открыта давно (emergency/live.ts; код-сплит).
    void importChunk(() => import('./emergency/live')).then(({ startAlertWatch }) =>
      startAlertWatch(parsed),
    )
    // Бейдж «Организация проверена» (N2) — лениво, вне критического пути.
    void fetch(`/api/venue-badge/${parsed.venue.id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((badge: { verified?: boolean } | null) => {
        venueVerified.value = badge?.verified === true
      })
      .catch(() => undefined)
    // Аварийный режим: ?e= присутствует И площадка имеет ключи → код-сплит (G4).
    void maybeActivateEmergency(parsed, location.search)
    // Демо-режим ЧС (?drill=fire|flood|evac|uav) — всегда с водяным знаком учений.
    void maybeActivateDrill(parsed, location.search)
  } catch (error) {
    console.error('venue load failed', error)
    appState.value = 'error'
  }
}

/**
 * Lazy-загрузка и верификация аварийного кадра (G4, ADR 0009).
 * Код-сплит: emergency-чанк не попадает в критический путь загрузки.
 * Невалидный/отсутствующий параметр → молча игнорируем (анти-паника).
 */
async function maybeActivateEmergency(parsed: Venue, search: string): Promise<void> {
  const params = new URLSearchParams(search)
  const eParam = params.get('e')
  if (!eParam || !parsed.evacuation?.keys?.length) return
  try {
    const { verifyEmergencyParam } = await importChunk(() => import('./emergency/verify'))
    const frame = await verifyEmergencyParam(eParam, parsed)
    if (frame) emergencyFrame.value = frame
  } catch {
    // Молча игнорируем: ошибка верификации = не активируем режим
  }
}

async function maybeActivateDrill(parsed: Venue, search: string): Promise<void> {
  const { parseDrillParam, buildDrillFrame } = await importChunk(() => import('./emergency/drill'))
  const hazard = parseDrillParam(search)
  if (!hazard) return
  emergencyHazard.value = hazard
  emergencyDrill.value = true
  emergencyFrame.value = buildDrillFrame(parsed.venue.id, hazard)
}

if (!isDirectory && !isPromoRanking) void loadVenue(link.venueId)
